import { CareerIconName } from 'atoms/CareerIcon/CareerIcon';
import { ICareerAction } from 'core/hooks/CareerJourney/transform/transformCareerJourney';
import { formatCareerDate } from 'services/DateService';

type Translate = (key: string, vars?: Record<string, unknown>) => string;

/**
 * Подписи действий карьерного плана (ТЗ 0071, п. 4.4 и 9.2).
 *
 * 🔴 Приоритет выбирает сервер. Здесь только ЯЗЫК: по виду и причине
 * подбираются заголовок, польза и надпись кнопки, а данные — урок, «6 из 7»,
 * минуты, результат, компания — приходят с сервера как есть.
 *
 * Ключи записаны ЛИТЕРАЛАМИ, а не собраны из `kind`: сканер паритета локалей
 * (`locales/__tests__/careerCenter.locale.test.ts`) видит только литералы, и
 * новый вид без польской подписи иначе уехал бы студенту сырым ключом — ровно
 * так на стенде показалось `careerCenter.journey.next.align_profile`.
 */
interface IActionCopy {
  icon: CareerIconName;
  title: string;
  reason: string;
  cta: string;
}

const COPY: Record<string, IActionCopy> = {
  post_hire: {
    icon: 'offer',
    title: 'careerCenter.plan.action.post_hire.title',
    reason: 'careerCenter.plan.action.post_hire.reason',
    cta: 'careerCenter.plan.action.post_hire.cta',
  },
  placement_pending: {
    icon: 'offer',
    title: 'careerCenter.plan.action.placement_pending.title',
    reason: 'careerCenter.plan.action.placement_pending.reason',
    cta: 'careerCenter.plan.action.placement_pending.cta',
  },
  recruitment_task: {
    icon: 'interview',
    title: 'careerCenter.plan.action.recruitment_task.title',
    reason: 'careerCenter.plan.action.recruitment_task.reason',
    cta: 'careerCenter.plan.action.recruitment_task.cta',
  },
  goal: {
    icon: 'goal',
    title: 'careerCenter.plan.action.goal.title',
    reason: 'careerCenter.plan.action.goal.reason',
    cta: 'careerCenter.plan.action.goal.cta',
  },
  align_profile: {
    icon: 'cv',
    title: 'careerCenter.plan.action.align_profile.title',
    reason: 'careerCenter.plan.action.align_profile.reason',
    cta: 'careerCenter.plan.action.align_profile.cta',
  },
  continue_lesson: {
    icon: 'learning',
    title: 'careerCenter.plan.action.continue_lesson.title',
    reason: 'careerCenter.plan.action.continue_lesson.reason',
    cta: 'careerCenter.plan.action.continue_lesson.cta',
  },
  next_lesson: {
    icon: 'learning',
    title: 'careerCenter.plan.action.next_lesson.title',
    reason: 'careerCenter.plan.action.next_lesson.reason',
    cta: 'careerCenter.plan.action.next_lesson.cta',
  },
  portfolio: {
    icon: 'portfolio',
    title: 'careerCenter.plan.action.portfolio.title',
    reason: 'careerCenter.plan.action.portfolio.reason',
    cta: 'careerCenter.plan.action.portfolio.cta',
  },
  cv: {
    icon: 'cv',
    title: 'careerCenter.plan.action.cv.title',
    reason: 'careerCenter.plan.action.cv.reason',
    cta: 'careerCenter.plan.action.cv.cta',
  },
  critical_skill: {
    icon: 'star',
    title: 'careerCenter.plan.action.critical_skill.title',
    reason: 'careerCenter.plan.action.critical_skill.reason',
    cta: 'careerCenter.plan.action.critical_skill.cta',
  },
  find_vacancy: {
    icon: 'applications',
    title: 'careerCenter.plan.action.find_vacancy.title',
    reason: 'careerCenter.plan.action.find_vacancy.reason',
    cta: 'careerCenter.plan.action.find_vacancy.cta',
  },
  add_external_application: {
    icon: 'applications',
    title: 'careerCenter.plan.action.add_external_application.title',
    reason: 'careerCenter.plan.action.add_external_application.reason',
    cta: 'careerCenter.plan.action.add_external_application.cta',
  },
  prepare_interview: {
    icon: 'interview',
    title: 'careerCenter.plan.action.prepare_interview.title',
    reason: 'careerCenter.plan.action.prepare_interview.reason',
    cta: 'careerCenter.plan.action.prepare_interview.cta',
  },
  join_career_centre: {
    icon: 'lock',
    title: 'careerCenter.plan.action.join_career_centre.title',
    reason: 'careerCenter.plan.action.join_career_centre.reason',
    cta: 'careerCenter.plan.action.join_career_centre.cta',
  },
};

/** Причины, у которых свой заголовок или своя польза. */
const REASON_TITLE: Record<string, string> = {
  overdue_follow_up: 'careerCenter.plan.action.recruitment_task.titleOverdue',
  interview_scheduled: 'careerCenter.plan.action.recruitment_task.titleInterview',
  offer: 'careerCenter.plan.action.recruitment_task.titleOffer',
  skill_below_level: 'careerCenter.plan.action.critical_skill.titleBelow',
};

const REASON_TEXT: Record<string, string> = {
  overdue_follow_up: 'careerCenter.plan.action.recruitment_task.reasonOverdue',
  interview_scheduled: 'careerCenter.plan.action.recruitment_task.reasonInterview',
  offer: 'careerCenter.plan.action.recruitment_task.reasonOffer',
  no_interview_lesson: 'careerCenter.plan.action.prepare_interview.reasonNoLesson',
};

/** Какой пункт резюме закрыть — те же подписи, что у чек-листа резюме. */
const CV_CHECK_TITLE: Record<string, string> = {
  has_name: 'careerCenter.journey.task.cv_has_name',
  has_contact: 'careerCenter.journey.task.cv_has_contact',
  has_position: 'careerCenter.journey.task.cv_has_position',
  has_skills: 'careerCenter.journey.task.cv_has_skills',
  has_experience_or_project: 'careerCenter.journey.task.cv_has_experience_or_project',
  has_education: 'careerCenter.journey.task.cv_has_education',
  has_about: 'careerCenter.journey.task.cv_has_about',
};

const FALLBACK: IActionCopy = {
  icon: 'arrowRight',
  title: 'careerCenter.plan.action.fallback.title',
  reason: 'careerCenter.plan.action.fallback.reason',
  cta: 'careerCenter.plan.action.fallback.cta',
};

export interface IActionText {
  icon: CareerIconName;
  title: string;
  /** Польза — почему это стоит сделать сейчас. */
  reason: string;
  /** «Lekcja 6 z 7», срок и подобное — пусто, если данных нет. */
  meta: string[];
  /** Результат урока словами методиста. */
  outcome: string;
  /**
   * Одна строка под названием в карточке основного действия: «Efekt: …», если
   * результат есть, иначе польза. Причину начатого урока («dokończ, żeby
   * otworzyć następną») повторяет состояние «W trakcie» — второй раз её не пишем.
   */
  line: string;
  cta: string;
}

export const actionText = (t: Translate, action: ICareerAction): IActionText => {
  const copy = COPY[action.kind] ?? FALLBACK;
  const vars = {
    subject: action.subjectTitle,
    date: formatCareerDate(action.dueDate),
  };

  let title = t(REASON_TITLE[action.reasonCode] ?? copy.title, vars);
  // Урок называется своим именем: «Kontynuuj naukę» без урока — ровно то,
  // что ТЗ запрещает в п. 4.4.
  if ((action.kind === 'continue_lesson' || action.kind === 'next_lesson') && action.subjectTitle) {
    title = action.subjectTitle;
  }
  if (action.kind === 'prepare_interview' && action.subjectTitle) {
    title = action.subjectTitle;
  }
  if (action.kind === 'cv' && CV_CHECK_TITLE[action.reasonCode]) {
    title = t(CV_CHECK_TITLE[action.reasonCode]);
  }

  const meta: string[] = [];
  if (action.position && action.positionTotal) {
    meta.push(
      t('careerCenter.plan.meta.lesson', { n: action.position, total: action.positionTotal }),
    );
  }
  if (action.estimatedMinutes) {
    meta.push(t('careerCenter.plan.meta.minutes', { n: action.estimatedMinutes }));
  }
  if (action.dueDate && action.kind !== 'recruitment_task') {
    meta.push(t('careerCenter.plan.meta.due', { date: formatCareerDate(action.dueDate) }));
  }
  if (action.kind === 'continue_lesson') {
    meta.push(t('careerCenter.plan.meta.inProgress'));
  }

  const reason = t(REASON_TEXT[action.reasonCode] ?? copy.reason, vars);
  const outcome = action.expectedOutcome;

  return {
    icon: copy.icon,
    title,
    reason,
    meta,
    outcome,
    line: outcome ? t('careerCenter.plan.meta.outcome', { outcome }) : reason,
    cta: t(copy.cta),
  };
};

/** Виды, у которых есть подписи. Сторож в тесте сверяет их с серверными. */
export const KNOWN_ACTION_KINDS = Object.keys(COPY);
